import { useState, useEffect, useCallback } from "react";
import DynamicTable from "../../ui/DynamicTable";
import config from "../../../config/config";
import { refreshDashboardData } from "../../../observers/dataObserver";
import { refreshUserTableData } from "../../../observers/dataObserver";
import { BsThreeDotsVertical } from "react-icons/bs";
import Pagination from "../../ui/Pagination";
import { userSession } from "../../../observers/userObserver";
import ConfirmationModal from "../../ui/ConfirmationModal";

const UserTable = () => {
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [checked, setChecked] = useState({});
  const [aiSearchChecked, setAiSearchChecked] = useState({});
  const [rows, setRows] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_items: 0,
    total_pages: 0,
    limit: 10
  })

  const token = userSession.getToken();
  const getAuthHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  });









  const fetchCODData = useCallback(async () => {
    try {
      const response = await fetch(`${config["backend-url"]}/customer_users_data?page=${pagination.current_page}&limit=${pagination.limit}`, {
        headers: { Authorization:`Bearer ${token}` },
      });

      const data = await response.json();
      if (response.ok) {
        const users = data.data.map((user) => ({
          id: user.id,
          name: user.name,
          email: user.email,
          user_status: user.user_status,
          statusClass: user.statusClass,
          statusLabel: user.statusLabel,
          two_fa_enabled: user.two_fa_enabled,
          totp_enabled: user.totp_enabled,
        }));
        setChecked(data.data.reduce((acc, user) => ({ ...acc, [user.id]: user.MLNOFlag }), {}));
        setAiSearchChecked(data.data.reduce((acc, user) => ({ ...acc, [user.id]: user.ai_search_enabled }), {}));
        setPagination(data.pagination);
        setRows(users);
      } else {
        console.error("❌ Failed to load:", data.error);
      }
    } catch (error) {
      console.error("❌ Network or server error:", error);
    }
  }, [pagination.current_page, pagination.limit]);

  const toggleAISearch = useCallback(async (userId, currentStatus) => {
    const newStatus = !currentStatus;
    setAiSearchChecked((prev) => ({ ...prev, [userId]: newStatus }));
    try {
      const response = await fetch(`${config["backend-url"]}/toggle_ai_search`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: userId, enabled: newStatus }),
      });
      const data = await response.json();
      if (!data.success) {
        setAiSearchChecked((prev) => ({ ...prev, [userId]: currentStatus }));
        alert(data.error || "Failed to update AI Search");
      }
    } catch (error) {
      setAiSearchChecked((prev) => ({ ...prev, [userId]: currentStatus }));
      console.error("❌ Network error:", error);
    }
  }, [token]);

  const toggle2FA = useCallback(async (userId, enable) => {
    if (!window.confirm(`${enable ? "Enable" : "Disable"} 2FA for this user?`)) return;
    try {
      const response = await fetch(`${config["backend-url"]}/toggle_2fa`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ user_id: userId, enabled: enable }),
      });
      const data = await response.json();
      if (response.ok) {
        fetchCODData();
      } else {
        alert(`Failed to ${enable ? "enable" : "disable"} 2FA: ` + (data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("❌ Network error:", error);
    }
  }, [fetchCODData]);

  const resetUserTOTP = useCallback(async (userId) => {
    if (!window.confirm("Reset this user's authenticator app? They will need to set it up again on their next login.")) return;
    try {
      const response = await fetch(`${config["backend-url"]}/totp/reset`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await response.json();
      if (response.ok) {
        fetchCODData();
      } else {
        alert("Failed to reset TOTP: " + (data.error || "Unknown error"));
      }
    } catch (error) {
      console.error("❌ Network error:", error);
    }
  }, [fetchCODData]);

  const changeUserStatus = useCallback(async (userId, newStatus) => {
    setDropdownOpen({});
    try {
      const response = await fetch(`${config["backend-url"]}/Change_User_Status`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ user_id: userId, User_Status: newStatus }),
      });

      const data = await response.json();

      if (response.ok) {
        console.log("✅ Status updated:", data.message);
        refreshDashboardData.push("refresh");
        setPagination((prevPagination) => ({ ...prevPagination, current_page: 1 }));
        fetchCODData(); // Refresh data after status update
      } else {
        console.error("❌ Error updating status:", data.error);
        // Show modal if it's user limit exceed error
       // If error is user limit exceeded → show modal
   if (data.error?.toLowerCase().includes("user limit")) {
    setConfirmMessage(data.error);
    setShowConfirmModal(true);
  }
      }
    } catch (error) {
      console.error("❌ Network error:", error);
    }
  },[fetchCODData]);

  const saveMlnoFlag = useCallback(async (userId, status) => {
    try {
      const response = await fetch(`${config["backend-url"]}/update_mlno_flag`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ user_id: userId, mlno_flag: status }),
      });

      const result = await response.json();

      if (response.ok) {
        console.log("✅ MLNOFlag updated:", result); // BUG: Backend always says flag is true
      } else {
        console.error("❌ Failed:", result.error);
      }
    } catch (error) {
      console.error("❌ Network or server error:", error);
    }
  },[]);

  const toggleChecked = useCallback((id, value) => {
    setChecked((prev) => {
      return { ...prev, [id]: value };
    });
    saveMlnoFlag(id, value);
  },[saveMlnoFlag]);

  const handlePageChange = (pageNumber) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      current_page: pageNumber
    }));
  };

  useEffect(() => {
    fetchCODData();

    refreshUserTableData.subscribe((value) => {
      if (value === "refresh") {
        fetchCODData();
      }
    });

  }, [fetchCODData]);

  const renderStatus = useCallback((value) => (
    <span className={`status-tile status-${value}`}>{value}</span>
  ),[]);

  const toggleDropdown = useCallback((id) => {
    setDropdownOpen((prev) => {
        const isOpen = prev[id];
        return isOpen
        ? {} // Close all if current is open
        : { [id]: true }; // Open only the current one
    });
},[]);

  const renderActionDropdown = useCallback((_, row) => {
    return (
        <div className="dropdown-container" key={row.id}>
        <button
            onClick={() => toggleDropdown(row.id)}
            className="dropdown-btn"
        >
            <BsThreeDotsVertical size={20} />
        </button>
        {dropdownOpen[row.id] && (
            <div className="custom-dropdown">
            {row.user_status?.toLowerCase() === "pending" && (
                <>
                <div
                    className="dropdownItem"
                    onClick={() => changeUserStatus(row.id, "Active")}
                >
                    Activate
                </div>
                <div
                    className="dropdownItem"
                    onClick={() => changeUserStatus(row.id, "Inactive")}
                >
                    Deactivate
                </div>
                </>
            )}
            {row.user_status?.toLowerCase() === "active" && (
                <div
                className="dropdownItem"
                onClick={() => changeUserStatus(row.id, "Inactive")}
                >
                Deactivate
                </div>
            )}
            {row.user_status?.toLowerCase() === "inactive" && (
                <div
                className="dropdownItem"
                onClick={() => changeUserStatus(row.id, "Active")}
                >
                Activate
                </div>
            )}
            {!row.two_fa_enabled && (
                <div
                className="dropdownItem"
                onClick={() => toggle2FA(row.id, true)}
                >
                Enable 2FA
                </div>
            )}
            {row.two_fa_enabled && (
                <div
                className="dropdownItem"
                onClick={() => toggle2FA(row.id, false)}
                >
                Disable 2FA
                </div>
            )}
            {row.totp_enabled && (
                <div
                className="dropdownItem"
                onClick={() => resetUserTOTP(row.id)}
                >
                Reset TOTP
                </div>
            )}
            </div>
        )}
        </div>
    );
    },[changeUserStatus, toggle2FA, resetUserTOTP, dropdownOpen, toggleDropdown]);


  const renderMLNOCheckbox = useCallback((_, row) => {
    const isChecked = checked[row.id] || false;
    return (
      <input
        type="checkbox"
        checked={isChecked}
        onChange={() => toggleChecked(row.id, !isChecked)}
        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
      />
    );
  }, [checked, toggleChecked]);

  const render2FAStatus = useCallback((_, row) => {
    if (!row.two_fa_enabled) return <span style={{ color: "#aaa", fontSize: "0.75rem" }}>Off</span>;
    if (row.totp_enabled) return (
      <span style={{ background: "#002961", color: "#fff", borderRadius: "4px", padding: "2px 7px", fontSize: "0.75rem", fontWeight: 600 }}>
        TOTP
      </span>
    );
    return (
      <span style={{ background: "#6c757d", color: "#fff", borderRadius: "4px", padding: "2px 7px", fontSize: "0.75rem", fontWeight: 600 }}>
        Email OTP
      </span>
    );
  }, []);

  const renderAISearchCheckbox = useCallback((_, row) => {
    const isEnabled = aiSearchChecked[row.id] || false;
    return (
      <input
        type="checkbox"
        checked={isEnabled}
        onChange={() => toggleAISearch(row.id, isEnabled)}
        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
      />
    );
  }, [aiSearchChecked, toggleAISearch]);

  const columns = [
    { key: "name", label: "User Name" },
    { key: "email", label: "Email" },
    { key: "user_status", label: "Status", render: renderStatus },
    { key: "two_fa_enabled", label: "2FA", render: render2FAStatus },
    { key: "action", label: "Action", render: renderActionDropdown },
    { key: "MLNOFlag", label: "MLNO Flag", render: renderMLNOCheckbox },
    { key: "ai_search_enabled", label: "AI Search", render: renderAISearchCheckbox },
  ];

  return (
    <>
      <DynamicTable data={rows} columns={columns} />
      <Pagination totalPages={pagination.total_pages} page={pagination.current_page} handlePageChange={handlePageChange} />
    <ConfirmationModal
  isOpen={showConfirmModal}
  message={confirmMessage}
  onClose={() => setShowConfirmModal(false)}
/>

    </>
  );
};

export default UserTable;
